import type { CostAllocationDetail } from "../api/costAllocationsApi";
import { formatMoney } from "../utils/formatMoney";

type Props = {
  allocations: CostAllocationDetail["allocations"];
  totalAmount: string;
};

export function AllocationsBreakdownTable({ allocations, totalAmount }: Props) {
  // Sanity invariant: la suma del breakdown debe igualar el monto del costo.
  // Sumamos en céntimos enteros para evitar IEEE-754 drift al re-agregar.
  const sumCents = allocations.reduce(
    (acc, a) => acc + Math.round(Number.parseFloat(a.amount) * 100),
    0,
  );
  const totalCents = Math.round(Number.parseFloat(totalAmount) * 100);
  const matches = sumCents === totalCents;

  return (
    <section
      aria-label="Distribución por animal"
      className="rounded-2xl border border-app-border bg-app-surface"
    >
      <header className="flex items-center justify-between border-app-border border-b px-4 py-3">
        <div>
          <h2 className="font-medium text-[14px] text-app-ink-1">Distribución por animal</h2>
          <p className="text-[12px] text-app-ink-3">
            {allocations.length} {allocations.length === 1 ? "animal" : "animales"} · prorrateo por
            días-animal
          </p>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-app-ink-3 uppercase tracking-wide">Total</div>
          <div className="font-semibold text-[15px] text-app-ink-1 tabular-nums">
            {formatMoney(totalAmount)}
          </div>
        </div>
      </header>

      <ul className="divide-y divide-app-border">
        {allocations.map((a) => (
          <li key={a.id} className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="font-medium font-mono text-[13px] text-app-ink-1">{a.animalTag}</div>
              {a.animalName && (
                <div className="truncate text-[12px] text-app-ink-3">{a.animalName}</div>
              )}
            </div>
            <div className="shrink-0 font-medium text-[14px] text-app-ink-1 tabular-nums">
              {formatMoney(a.amount)}
            </div>
          </li>
        ))}
      </ul>

      {!matches && (
        // Caso defensivo: si el server alguna vez devuelve un breakdown
        // que no suma el total, mostramos un warning visible. No debería
        // pasar (D2 garantiza la invariante), pero el costo de detectarlo
        // es nulo y evita confundir al contador en silencio.
        <div
          role="alert"
          className="border-app-border border-t bg-amber-50 px-4 py-3 text-[12px] text-amber-800"
        >
          Inconsistencia: la suma del prorrateo ({formatMoney((sumCents / 100).toFixed(2))}) no
          coincide con el total registrado ({formatMoney(totalAmount)}). Reportalo al equipo.
        </div>
      )}
    </section>
  );
}
